import { SimpleObservable, SimpleObservableSubject } from "@/utils/observable";

import {
  AppKit,
  createAppKit,
  type ConnectedWalletInfo,
  type SIWXSession,
  type UseAppKitAccountReturn,
} from "@reown/appkit";
import { chiliz } from "@reown/appkit/networks";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { getUrlOrAbsoluteUrlFromOrigin } from "@/utils/misc";
import { AppSiwe } from "./app-siwe";
import { createSIWEConfig } from "@reown/appkit-siwe";
import { EXCLUDED_WALLET_IDS, SUPPORTED_WALLETS } from "./app-kit-constants";

export const networks = [chiliz];
// TODO: Put into a config file
const projectId = "e64a37d300c1ee82afe7d93e292778ce";

const metadata = {
  name: "Fanfest",
  description: "AppKit Example",
  url: window.origin,
  icons: [getUrlOrAbsoluteUrlFromOrigin("/logo.png")],
};

export type AppKitSession = SIWXSession;

export class AppKitConnector extends SimpleObservable {
  public onAccount = new SimpleObservableSubject<UseAppKitAccountReturn>();
  public onWalletInfo = new SimpleObservableSubject<
    ConnectedWalletInfo | undefined
  >();

  public onWebThreeToken: AppSiwe["onWebThreeToken"];
  public onWalletConnectedInSession: AppSiwe["onWalletConnectedInSession"];

  protected appSiwe: AppSiwe;
  protected appKit: AppKit | null;
  protected wagmiAdapter: WagmiAdapter | null;

  constructor(
    appSiwe = new AppSiwe(),
    appKit: AppKit | null = null,
    wagmiAdapter: WagmiAdapter | null = null,
  ) {
    super();

    this.appSiwe = appSiwe;
    this.onWebThreeToken = appSiwe.onWebThreeToken;
    this.onWalletConnectedInSession = appSiwe.onWalletConnectedInSession;
    this.appKit = appKit;
    this.wagmiAdapter = wagmiAdapter;
  }

  public async init() {
    const wagmiAdapter = new WagmiAdapter({
      projectId,
      networks,
    });

    const appKit = createAppKit({
      adapters: [wagmiAdapter],
      networks: [chiliz],
      metadata,
      projectId,
      enableWalletConnect: true,
      enableNetworkSwitch: false,
      enableInjected: false,
      enableWalletGuide: false,
      enableEIP6963: false,
      enableCoinbase: false,
      defaultAccountTypes: { eip155: "eoa" },
      // includeWalletIds: SUPPORTED_WALLETS,
      // No need to exclude anything when you include-only, but leaving explicit for clarity
      // AppKit 1.7.10 requests /getWallets?entries=0 when this option is [];
      // omit it until there are IDs to exclude.
      ...(EXCLUDED_WALLET_IDS.length > 0
        ? { excludeWalletIds: EXCLUDED_WALLET_IDS }
        : {}),
      // Optional: put Socios on the modal’s first screen
      featuredWalletIds: SUPPORTED_WALLETS,
      allowUnsupportedChain: false,
      features: {
        analytics: true,
        allWallets: true,
      },
      siweConfig: createSIWEConfig(this.appSiwe),
    });

    appKit.subscribeAccount((account) => {
      this.notify(this.onAccount, account);
    });

    appKit.subscribeWalletInfo((walletInfo) => {
      this.notify(this.onWalletInfo, walletInfo);
    });

    this.wagmiAdapter = wagmiAdapter;
    this.appKit = appKit;

    return appKit;
  }

  public readonly chainNamespace = "eip155";

  public async dispose() {
    const appKit = await this.getInstance();

    appKit.close();
  }

  public async logout() {
    await this.appSiwe.signOut();
    await this.dispose();
  }

  public async triggerActiveSessionOrAuth() {
    // TODO: Check if we need to trigger the active session
    const appKit = await this.getInstance();

    await appKit.disconnect();
    appKit.open();
  }

  protected async getInstance() {
    if (!this.appKit) {
      return await this.init();
    }

    return this.appKit;
  }
}
